<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  active: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['click'])

// Particle system for "jinbi" (gold coins)
interface Coin {
  id: number
  x: number
  y: number
  rotation: number
  scale: number
  opacity: number
  vx: number
  vy: number
}

const coins = ref<Coin[]>([])
let coinId = 0
let timer: ReturnType<typeof setInterval> | null = null

const spawnCoin = () => {
  const angle = (Math.random() - 0.5) * 0.6 - Math.PI / 2 // Upward direction with small spread
  const speed = 4 + Math.random() * 5
  coins.value.push({
    id: coinId++,
    x: 0,
    y: 0,
    rotation: Math.random() * 360,
    scale: (0.4 + Math.random() * 0.4) * 0.3, // Apply 0.3x base scale as requested
    opacity: 1,
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
  })

  if (coins.value.length > 30) {
    coins.value.shift()
  }
}

const updateCoins = () => {
  coins.value.forEach((coin) => {
    coin.x += coin.vx
    coin.y += coin.vy
    coin.vy += 0.35
    coin.opacity -= 0.015
    coin.rotation += 8
  })
  coins.value = coins.value.filter((c) => c.opacity > 0 && c.y < 300)
}

const startTime = Date.now()
const loopDuration = 6200 // 6.2 seconds cycle
let hasSpawnedInCycle = false

onMounted(() => {
  timer = setInterval(() => {
    const elapsed = Date.now() - startTime
    const cycleTime = elapsed % loopDuration
    
    // Logic: Coins pop after the pointer returns (at 6.0s mark)
    if (cycleTime >= 6000 && !hasSpawnedInCycle) {
      // Burst spawn 10 coins
      for (let i = 0; i < 10; i++) {
        spawnCoin()
      }
      hasSpawnedInCycle = true
    }
    
    // Reset spawn flag at start of new cycle
    if (cycleTime < 100) {
      hasSpawnedInCycle = false
    }

    updateCoins()
  }, 30)
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})
</script>

<template>
  <div class="turntable-wrapper" @click="emit('click')">
    <div class="layers-container">
      <!-- Coin Emitter Layer moved behind the wheel -->
      <div class="coin-emitter">
        <div
          v-for="coin in coins"
          :key="coin.id"
          class="coin-particle"
          :style="{
            transform: `translate(${coin.x}px, ${coin.y}px) rotate(${coin.rotation}deg) scale(${coin.scale})`,
            opacity: coin.opacity,
          }"
        ></div>
      </div>

      <!-- hall_pinBtn_2 should be at the bottom and rotating -->
      <img src="@/assets/hall/hallTop/hall_pinBtn_2.webp" class="layer wheel" />
      <!-- hall_pinBtn_4 in the middle -->
      <img src="@/assets/hall/hallTop/hall_pinBtn_4.webp" class="layer center-pointer" />
      <!-- hall_pinBtn_1 at the very top -->
      <img src="@/assets/hall/hallTop/hall_pinBtn_1.webp" class="layer frame" />

      <div class="wheel-text-overlay" :class="{ inactive: !props.active }">
        <div class="small-text">Free</div>
        <div class="large-text">Free 2000Ks</div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.turntable-wrapper {
  position: relative;
  width: 90px;
  height: 90px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 10;
}

.layers-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.layer {
  position: absolute;
  object-fit: contain;
}

.wheel {
  width: 100%; /* Base size */
  z-index: 1;
  /* Total duration 6.2s: act1(4s) + act2(2s) + act3(0.2s) */
  animation: rotate-wheel 6.2s linear infinite;
}

.center-pointer {
  width: 100%; /* Base size to match others, then scale */
  z-index: 2;
  pointer-events: none;
  /* Total duration 6.2s: act4(4s) + act5(0.2s) + act6(1.6s) + act7(0.2s) + act8(pop) + act9(0.2s) */
  animation: kick-pointer 6.2s linear infinite;
}

.frame {
  width: 100%;
  z-index: 3;
  pointer-events: none;
  transform: scale(0.7);
}

.wheel-text-overlay {
  position: absolute;
  bottom: -15px;
  width: 120px;
  text-align: center;
  z-index: 4;
  transition:
    filter 0.3s,
    opacity 0.3s;
}

.wheel-text-overlay.inactive {
  filter: grayscale(0.8);
  opacity: 0.7;
}

.small-text {
  font-size: 0.6rem;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
}

.large-text {
  font-size: 0.8rem;
  font-weight: bold;
  color: var(--theme-accent);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 1);
}

/* Particles */
.coin-emitter {
  position: absolute;
  top: 55%; /* Moved even lower as requested */
  left: 50%;
  pointer-events: none;
  z-index: 0; /* Behind everything */
  will-change: transform;
  /* Masking: allows coins to fly UP and sideways infinitely, but clips them if they fall too far below the bar */
  clip-path: inset(-1000px -1000px -40px -1000px);
}

.coin-particle {
  position: absolute;
  width: 118px;
  height: 102px;
  /* Centering the 118x102 particle on the emission point (50%, 40%) */
  margin-left: -59px;
  margin-top: -51px;
  background-image: url('@/assets/spine/jinbi.png');
  /* Extracting frame images/q_00009 from jinbi.atlas */
  background-position: -871px -2px;
  background-size: 1847px 1798px;
  pointer-events: none;
}

@keyframes rotate-wheel {
  /* 0s to 4s: Rotate 360 (Clockwise) */
  0% { transform: scale(0.62) rotate(0deg); }
  64.5% { transform: scale(0.62) rotate(360deg); }
  /* 4s to 6s: Rotate another 720 (totals 1080) */
  96.7% { transform: scale(0.62) rotate(1080deg); }
  /* 6s to 6.2s: Delay */
  100% { transform: scale(0.62) rotate(1080deg); }
}

@keyframes kick-pointer {
  /* 0s to 4s: Delay */
  0% { transform: scale(0.36) rotate(0deg); }
  64.5% { transform: scale(0.36) rotate(0deg); }
  /* 4s to 4.2s: Kick 70 (To the right) */
  67.7% { transform: scale(0.36) rotate(70deg); }
  /* 4.2s to 5.8s: Delay */
  93.5% { transform: scale(0.36) rotate(70deg); }
  /* 5.8s to 6s: Return to 0 */
  96.7% { transform: scale(0.36) rotate(0deg); }
  /* 6s to 6.2s: Delay */
  100% { transform: scale(0.36) rotate(0deg); }
}
</style>
